import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import { API_ENDPOINT } from '@xver/constants/env';
import storage from '@xver/utils/storage';
import {
    chunkTerminalInput,
    encodeTerminalInput,
    encodeTerminalResize,
} from '@xver/utils/terminalProtocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import '@xterm/xterm/css/xterm.css';

export type TerminalStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/** Matches the keepalive cadence Proxmox's own terminal client uses. */
const PING_INTERVAL_MS = 30_000;

/**
 * How long to wait after connecting before asking the guest to redraw its prompt.
 * Long enough for the serial terminal's own attach output to land first.
 */
const PROMPT_NUDGE_DELAY_MS = 300;

interface UseInstanceTerminalOptions {
    instanceId: string | number;
    /** Connect as soon as the terminal is mounted. Set false to require a click. */
    autoConnect: boolean;
    screenReaderMode: boolean;
    onServerClose?: () => void;
}

interface UseInstanceTerminalReturn {
    status: TerminalStatus;
    error: string | null;
    attachContainer: (node: HTMLDivElement | null) => void;
    connect: () => void;
    disconnect: () => void;
    clear: () => void;
    focus: () => void;
    blur: () => void;
    fit: () => void;
}

function toWebSocketBaseUrl(httpUrl: string): string {
    if (httpUrl.startsWith('https://')) {
        return 'wss://' + httpUrl.slice('https://'.length);
    }
    if (httpUrl.startsWith('http://')) {
        return 'ws://' + httpUrl.slice('http://'.length);
    }
    return httpUrl;
}

export function buildInstanceTerminalUrl(instanceId: string | number, token: string): string {
    const base = toWebSocketBaseUrl(API_ENDPOINT);
    return `${base}/v1/instances/${instanceId}/terminal/ws?token=${encodeURIComponent(token)}`;
}

/**
 * Drives an xterm.js terminal against the instance serial console.
 *
 * The Proxmox authentication handshake happens server-side, so this only speaks
 * the input/resize/ping framing. Pass the returned attachContainer to the host
 * element; the terminal is created on attach and disposed on unmount.
 */
export default function useInstanceTerminal({
    instanceId,
    autoConnect,
    screenReaderMode,
    onServerClose,
}: UseInstanceTerminalOptions): UseInstanceTerminalReturn {
    const [status, setStatus] = useState<TerminalStatus>('idle');
    const [error, setError] = useState<string | null>(null);

    const terminalRef = useRef<Terminal | null>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const controllerRef = useRef<AbortController | null>(null);

    const screenReaderModeRef = useRef(screenReaderMode);
    const onServerCloseRef = useRef<(() => void) | undefined>(onServerClose);
    useEffect(() => {
        onServerCloseRef.current = onServerClose;
    }, [onServerClose]);

    const send = useCallback((payload: string) => {
        const socket = socketRef.current;
        if (socket?.readyState === WebSocket.OPEN) socket.send(payload);
    }, []);

    const openStream = useCallback(
        (signal: AbortSignal) => {
            const token = storage.get<string>('_zugang', '');
            if (!token) {
                setStatus('error');
                setError('missing session token');
                return;
            }

            setStatus('connecting');
            setError(null);

            const socket = new WebSocket(buildInstanceTerminalUrl(instanceId, token));
            socket.binaryType = 'arraybuffer';
            socketRef.current = socket;

            let ping: ReturnType<typeof setInterval> | undefined;
            let promptNudge: ReturnType<typeof setTimeout> | undefined;

            const abortHandler = () => {
                if (socket.readyState !== WebSocket.CLOSED) socket.close();
            };
            signal.addEventListener('abort', abortHandler);

            socket.onopen = () => {
                if (signal.aborted) return;
                setStatus('open');

                const terminal = terminalRef.current;
                if (terminal) socket.send(encodeTerminalResize(terminal.cols, terminal.rows));

                // A serial console attaches to a session that already printed its
                // prompt, so reconnecting shows a blank screen until the guest
                // writes again. Nudge it with a carriage return, exactly what the
                // user would otherwise have to press. Delayed so it lands after
                // the getty's own attach output rather than racing it.
                promptNudge = setTimeout(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(encodeTerminalInput('\r'));
                    }
                }, PROMPT_NUDGE_DELAY_MS);

                ping = setInterval(() => socket.send('2'), PING_INTERVAL_MS);
            };

            socket.onmessage = (event) => {
                if (signal.aborted) return;
                const terminal = terminalRef.current;
                if (!terminal) return;

                if (event.data instanceof ArrayBuffer) {
                    terminal.write(new Uint8Array(event.data));
                } else {
                    terminal.write(String(event.data));
                }
            };

            socket.onerror = () => {
                if (signal.aborted) return;
                setStatus('error');
                setError((prev) => prev ?? 'websocket error');
            };

            socket.onclose = () => {
                if (ping) clearInterval(ping);
                if (promptNudge) clearTimeout(promptNudge);
                signal.removeEventListener('abort', abortHandler);
                if (socketRef.current === socket) socketRef.current = null;
                if (signal.aborted) return;
                setStatus((prev) => (prev === 'error' ? prev : 'closed'));
                onServerCloseRef.current?.();
            };
        },
        [instanceId]
    );

    const disconnect = useCallback(() => {
        const controller = controllerRef.current;
        if (controller && !controller.signal.aborted) controller.abort();
        controllerRef.current = null;
        setStatus((prev) => (prev === 'error' ? prev : 'closed'));
    }, []);

    const connect = useCallback(() => {
        if (controllerRef.current && !controllerRef.current.signal.aborted) {
            controllerRef.current.abort();
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        openStream(controller.signal);
    }, [openStream]);

    // Created on attach rather than at render so StrictMode's double mount cannot
    // leave an orphaned renderer and a second helper textarea in the a11y tree.
    const attachContainer = useCallback(
        (node: HTMLDivElement | null) => {
            if (!node) return;
            if (terminalRef.current) return;

            const terminal = new Terminal({
                convertEol: false,
                cursorBlink: true,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 13,
                screenReaderMode: screenReaderModeRef.current,
                scrollback: 5000,
                theme: { background: '#000000' },
            });
            const fitAddon = new FitAddon();
            terminal.loadAddon(fitAddon);
            terminal.open(node);

            terminal.onData((data) => {
                for (const chunk of chunkTerminalInput(data)) {
                    send(encodeTerminalInput(chunk));
                }
            });

            terminal.onResize(({ cols, rows }) => send(encodeTerminalResize(cols, rows)));

            // A terminal swallows Tab, which would trap keyboard users inside it.
            // Returning false leaves Shift+Tab to the browser so focus can escape.
            terminal.attachCustomKeyEventHandler(
                (event) => !(event.key === 'Tab' && event.shiftKey)
            );

            terminalRef.current = terminal;
            fitAddonRef.current = fitAddon;

            // The renderer has not measured a cell yet on the first paint, so fit()
            // would no-op. Proxmox's own client defers the same way.
            requestAnimationFrame(() => requestAnimationFrame(() => fitAddonRef.current?.fit()));
        },
        [send]
    );

    useEffect(() => {
        screenReaderModeRef.current = screenReaderMode;
        if (terminalRef.current) terminalRef.current.options.screenReaderMode = screenReaderMode;
    }, [screenReaderMode]);

    const fit = useCallback(() => {
        fitAddonRef.current?.fit();
    }, []);

    const focus = useCallback(() => {
        terminalRef.current?.focus();
    }, []);

    const blur = useCallback(() => {
        terminalRef.current?.blur();
    }, []);

    const clear = useCallback(() => {
        terminalRef.current?.clear();
    }, []);

    useEffect(() => {
        if (!autoConnect) return;
        const controller = new AbortController();
        controllerRef.current = controller;
        openStream(controller.signal);
        return () => {
            controller.abort();
            if (controllerRef.current === controller) controllerRef.current = null;
            // socket.onclose bails out early on an aborted signal, so nothing else
            // reports this. Without it the header keeps showing "Connected" over a
            // dead socket and silently swallows every keystroke.
            setStatus((prev) => (prev === 'error' ? prev : 'closed'));
        };
    }, [autoConnect, openStream]);

    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
            controllerRef.current = null;
            socketRef.current = null;
            fitAddonRef.current?.dispose();
            fitAddonRef.current = null;
            terminalRef.current?.dispose();
            terminalRef.current = null;
        };
    }, []);

    return { status, error, attachContainer, connect, disconnect, clear, focus, blur, fit };
}
